import { useQuery, useMutation} from '@tanstack/react-query'
import { 
        getMemberships, getMembershipRequests, 
        fetchMemberByID, addCommunityMembers,
        updateCommunityMembership, getMemberReviews,
        addMembershipCredentials, fetchMemberCredentials, getMemberRequirementsStatus,
        createBooking, updateBookingData,
        fetchCommunityBookings, fetchResourceBookings, fetchBookingByID, getAllMessages,
        sendMessageToUser, generateAIResponse, fetchCommunityTransactions,
        fetchPaymentMethods, updateMemberRequirementCredential
     } from '../api/crud'
import { MembershipSchema, MessageSchema, GroupedMessageListType, CredentialSchema, ResourceType, ResourceBookingSchema, CurrentRequirementsSchema } from "../data/types";
import { useMemberState } from '../contexts/membercontext'


const staleTime = 1000
const isEnabled = true

export const useLoadRequests = () => {
    let { communityId, setMembershipRequests } = useMemberState()
    const {data: membershipRequests, refetch: reloadRequests, isLoading: isLoadingRequests} = useQuery({ 
        queryKey: ['membershipRequests', communityId], 
        queryFn: async () => {
            if (communityId){
                console.log("fetching membership requests")
                const res = await getMembershipRequests(communityId)
                setMembershipRequests(res)
                return res
            }
            return []
        },
        staleTime,
        enabled: isEnabled
    })
    return {isLoadingRequests, membershipRequests, reloadRequests}
}


export const useLoadMemberships = () => {
    let { communityId, setMemberships } = useMemberState()
    const {data: memberships, refetch: reloadMemberships, isLoading: isLoadingMemberships} = useQuery({ 
        queryKey: ['memberships', communityId], 
        queryFn: async () => {
            if (communityId){
                console.log("fetching all memberships")
                const res = await getMemberships(communityId)
                setMemberships(res)
                return res
            }
            return []
        },
        staleTime,
        enabled: isEnabled
    })
    return {isLoadingMemberships, memberships, reloadMemberships}
}


export const useLoadMember = (uid: string) => {
    let { communityId, updateCurrentMember } = useMemberState()
    const {data: currentMember, isLoading: isLoadingCurrentMember} = useQuery({ 
        queryKey: ['currentMember', communityId, uid], 
        queryFn: async () => {
            console.log("fetching membership by uid")
            const res = await fetchMemberByID(communityId, uid)
            // console.log("res", res)
            updateCurrentMember(res)
            return res
        }
    })
    return {isLoadingCurrentMember, currentMember}
}


export const useAddMembership = () => {
    let { communityId } = useMemberState()
    const { reloadMemberships } = useLoadMemberships()
    const { reloadRequests } = useLoadRequests()

    const mutation = useMutation({ 
        mutationFn: async (membership: MembershipSchema[]) => {
            console.log("add member")
            const res = await addCommunityMembers(communityId, membership)
            await reloadMemberships()
            await reloadRequests()
            return res
    }})

    const addMembership = (membershipUpdate: MembershipSchema[]) => {
        mutation.mutate(membershipUpdate)
        return mutation.isSuccess
    }

    const isLoadingMembership = mutation.isPending
    const isAddMembershipError = mutation.isError
    const isAddMembershipIdle = mutation.isIdle
    const isAddMembershipSuccess = mutation.isSuccess
    return { addMembership, isLoadingMembership, isAddMembershipSuccess, isAddMembershipError, isAddMembershipIdle }
}


export const useUpdateMembership = (memberUid: string) => {
    var { communityId } = useMemberState()
    const { reloadMemberships } = useLoadMemberships()
    const { reloadRequests } = useLoadRequests()

    const mutation = useMutation({ 
        mutationFn: async (updateData: MembershipSchema) => {
            console.log("update member")
            const res = await updateCommunityMembership(memberUid, communityId, updateData)
            // console.log("update member res", res)
            // reloadMemberships()
            // reloadRequests()
            return res
        },
        onSuccess: async () => {
            // console.log("update member success")
            await reloadMemberships()
            await reloadRequests()
        }
    })

    const updateMembership = async (membershipUpdate: MembershipSchema) => {
        const updated = mutation.mutateAsync(membershipUpdate)
        // console.log("updated", updated)
        return updated
    }

    const isLoadingMembership = mutation.isPending
    return { updateMembership, isLoadingMembership }
}


export const useAddCredential = () => {
    var { communityId } = useMemberState()
    type mutationProps = {
        uid: string
        credential: CredentialSchema
    }
    const mutation = useMutation({ 
        mutationFn: async ({uid, credential}: mutationProps) => {
            console.log("add member credentials")
            const res = await addMembershipCredentials(communityId, uid, credential)
            return res
    }})

    const addMemberCredential = (uid: string, credential: CredentialSchema) => {
        mutation.mutate({uid, credential})
        return mutation?.data ? true : false
    }

    const isLoadingCredential = mutation.isPending
    return { addMemberCredential, isLoadingCredential }
}


export const useGetCredentials = (memberUid: string) => {
    var { communityId } = useMemberState()

    const {data: memberCredentials, refetch: reloadCredentials, isLoading: isLoadingCredentials} = useQuery({ 
        queryKey: ['credentials', communityId, memberUid], 
        queryFn: async () => {
            console.log("fetching credentials")
            const res = await fetchMemberCredentials(communityId, memberUid)
            return res
        },
        staleTime,
        enabled: isEnabled
    })

    return { isLoadingCredentials, memberCredentials, reloadCredentials}
}


export const useGetMemberRequirementsStatus = (uid: string, communityId: string) => {

    type updateCredentialMutationProps = {
        credentialId: string
        updatedCredentialDetails: CurrentRequirementsSchema
    }


    const {data, refetch: reloadRequirementsMet, isLoading: isLoadingRequirementsMet} = useQuery({ 
        queryKey: ['MemberRequirementsStatus', uid], 
        queryFn: async () => {
            console.log("fetching memberRequirements statuses")
            const res = await getMemberRequirementsStatus(uid, communityId)
            return res
        },
        enabled: !!uid && !!communityId
    })

    const updateCredentialMutation = useMutation({ 
        mutationFn: async ({credentialId, updatedCredentialDetails}: updateCredentialMutationProps) => {
            const res = await updateMemberRequirementCredential(uid, communityId, credentialId, updatedCredentialDetails)
            reloadRequirementsMet()
            return res
    }})

    const approveRequirementCredential = (credentialId: string, updatedCredentialDetails: CurrentRequirementsSchema) => {
        updateCredentialMutation.mutate({credentialId, updatedCredentialDetails})
        return updateCredentialMutation.isSuccess
    }

    const rejectRequirementCredential = (credentialId: string) => {
        updateCredentialMutation.mutate({credentialId, updatedCredentialDetails: {is_met: false} as CurrentRequirementsSchema})
        return updateCredentialMutation.isSuccess
    }

    const memberRequirementStatuses = Array.isArray(data) ? data : []
    return {memberRequirementStatuses, isLoadingRequirementsMet, approveRequirementCredential, rejectRequirementCredential, reloadRequirementsMet}
}


export const useGetMemberReviews = (memberUid: string) => {
    const {data: memberReviews, refetch: reloadReviews, isLoading: isLoadingReviews} = useQuery({ 
        queryKey: ['reviews', memberUid], 
        queryFn: async () => {
            console.log("fetching member reviews")
            const res = await getMemberReviews(memberUid)
            return res
        },
        staleTime,
        enabled: isEnabled
    })

    return { isLoadingReviews, memberReviews, reloadReviews}
}


export const  useLoadResourceBookings = (resourceId: string) => {
    let { communityId, updateBookings } = useMemberState()
    const {data: resourceBookings, refetch: reloadResourceBookings, isLoading: isLoadingResourceBookings} = useQuery({ 
        queryKey: ['resource_bookings', communityId, resourceId], 
        queryFn: async () => {
            console.log("fetching resource bookings")
            const res = await fetchResourceBookings(communityId, resourceId)
            updateBookings(res)
            return res
        },
        staleTime,
        enabled: isEnabled
    })
    return { isLoadingResourceBookings, resourceBookings, reloadResourceBookings }
}

export const useLoadCommunityBookings = (resourceType: ResourceType) => {
    let { communityId } = useMemberState()

    const {data: bookings, refetch: reloadBookings, isLoading: isLoadingBookings} = useQuery({ 
        queryKey: ['bookings', communityId, resourceType], 
        queryFn: async () => {
            console.log("fetching community bookings (by resource type)")
            const res = await fetchCommunityBookings(communityId, resourceType)
            return res
        },
        staleTime,
        enabled: isEnabled
    })
    return { isLoadingBookings, bookings, reloadBookings }
}


export const useLoadCommunityTransactions = (resourceType: ResourceType) => {
    let { communityId } = useMemberState()

    const {data: transactions, refetch: reloadTransactions, isLoading: isLoadingTransactions} = useQuery({ 
        queryKey: ['resource_transactions', communityId, resourceType], 
        queryFn: async () => {
            console.log("fetching community transactions (by resource type)")
            const res = await fetchCommunityTransactions(communityId, resourceType)
            return res
        },
        staleTime,
        enabled: isEnabled
    })
    return { isLoadingTransactions, transactions, reloadTransactions }
}


export const useLoadBooking = (bookingId: string) => {
    let { communityId, updateCurrentBooking } = useMemberState()
    const {data: currentBooking, refetch: reloadCurrentBooking, isLoading: isLoadingBooking} = useQuery({ 
        queryKey: ['currentBooking', communityId, bookingId], 
        queryFn: async () => {
            console.log("fetching booking by id")
            const res = await fetchBookingByID(communityId, bookingId)
            updateCurrentBooking(res)
            return res
        },
        staleTime,
        enabled: isEnabled
    })
    return { isLoadingBooking, currentBooking, reloadCurrentBooking}
}



export const useAddBooking = (resourceType: ResourceType) => {
    const { reloadBookings } = useLoadCommunityBookings(resourceType)

    const mutation = useMutation({ 
        mutationFn: async ({bookingData, resourceId}: {bookingData: ResourceBookingSchema, resourceId: string}) => {
            console.log("add booking") 
            const res = await createBooking(resourceId, bookingData)
            reloadBookings()
            return res
    }})

    const addNewBooking = (resourceId: string, bookingData: ResourceBookingSchema) => {
        mutation.mutate({bookingData, resourceId})
        return mutation?.data ? true : false
    }

    const isLoadingBooking = mutation.isPending
    const isAddBookingSuccess = mutation.isSuccess
    const isAddBookingError = mutation.isError
    return { addNewBooking, isLoadingBooking, isAddBookingSuccess, isAddBookingError }
}


export const useUpdateBooking = (bookingId: string, resourceId?: string, resourceType?: ResourceType) => {
    var { communityId } = useMemberState()
    var { reloadResourceBookings } = useLoadResourceBookings(resourceId || "")
    const { reloadBookings } = useLoadCommunityBookings(resourceType as ResourceType)
    // var { reloadBookings } = useLoadCommunityBookings(resourceType as ResourceType)

    const mutation = useMutation({ 
        mutationFn: async (updateData: ResourceBookingSchema) => {
            console.log("update booking") 
            const res = await updateBookingData(communityId, bookingId, updateData)
            // Reload both lists to ensure all views are updated
            // reloadResourceBookings for resource-specific view (ResourceDetails)
            // reloadBookings for community-wide view by type (ResourcesPage)
            return res
        },
        onSuccess: async () => {
            if (resourceId){
                await reloadResourceBookings()
            }
            if (resourceType){
                await reloadBookings()
            }
        }
    })

    const updateBooking = (bookingUpdate: ResourceBookingSchema) => {
        mutation.mutate(bookingUpdate)
        return mutation?.data ? true : false
    }

    const updateBookingAsync = async (bookingUpdate: ResourceBookingSchema) => {
        const res = await mutation.mutateAsync(bookingUpdate)
        return res ? true : false
    }

    const isLoadingBooking = mutation.isPending
    return { updateBooking, updateBookingAsync, isLoadingBooking }
}


export const useLoadMessages = () => {
    let {  communityId, currentChatMember,
            changeChatMember,
            updateMessageThread, updateAllMessages, 
            updateAllsThreadsByMember } = useMemberState()

    const handleGroupMessages = (allMessages: MessageSchema[]): GroupedMessageListType => {
        const groupMessages = allMessages.reduce((result: GroupedMessageListType, message: MessageSchema) => {
            const { uid } = message; // Destructure the object, excluding the 'uid' property
            if (uid && !result[uid]) {
                result[uid] = []; // Create an empty array for the uid if it doesn't exist
            }
            if (uid && result[uid]) {
                result[uid].push(message); // Add the object to the corresponding uid array
            }
            return result;
        }, {})
        return groupMessages
    }

    const {data: messages, refetch: reloadMessages, isLoading: isLoadingMessages} = useQuery({ 
        queryKey: ['messages', communityId], 
        queryFn: async () => {
            console.log("fetching messages")
            const res = await getAllMessages(communityId)
            updateAllMessages(res)

            let groupMessages = handleGroupMessages(res)
            updateAllsThreadsByMember(groupMessages)

            var chatbotMessages = groupMessages[currentChatMember]
            // if (!currentChatMember){
            //     changeChatMember('chatbot')
            //     chatbotMessages = groupMessages['chatbot']
            // }
            
            chatbotMessages = chatbotMessages ? chatbotMessages : []
            updateMessageThread(chatbotMessages)
            return res
        },
        staleTime,
        enabled: isEnabled
    })

    const switchMember = async (memberUid: string) => {
        if (memberUid !== currentChatMember){
            await changeChatMember(memberUid)
            reloadMessages()
        }
      
    }
    return {isLoadingMessages, messages, switchMember, reloadMessages}
}



export const useSendMessage = () => {
    var { communityId } = useMemberState()
    var { reloadMessages } = useLoadMessages()

    type messageMutationType = {
        uid: string
        content: string
    }

    const mutation = useMutation({ 
        mutationFn: async ({uid, content}: messageMutationType) => {
            console.log("send message") 
            if (uid === "chatbot"){
                const res = await generateAIResponse(communityId, uid, content) 
                await reloadMessages()
                return res
            } else {
                const res = await sendMessageToUser(communityId, uid, content) 
                await reloadMessages()
                return res
            }
            
    }})

    const sendMessage = (uid: string, content: string) => {
        mutation.mutate({uid, content})
        return mutation.isSuccess
    }

    const sendMessageAsync = (uid: string, content: string) =>
        mutation.mutateAsync({ uid, content })

    // const getAIMessage = (uid: string, content: string) => {
    //     mutation.mutate({uid, content})
    //     return true
    // }
    const isLoadingMessage = mutation.isPending
    return { sendMessage, sendMessageAsync, isLoadingMessage }
}



export const useGetPaymentMethods = (uid: string) => {
    // var {authUser}  = useAuthState()
    // let memberUsername = authUser?.username ? authUser.username : ''
    const {data: paymentMethods, refetch: reloadPaymentMethods, isLoading: isLoadingPaymentMethods} = useQuery({ 
        queryKey: ['payMethods', uid], 
        queryFn: async () => {
            console.log("fetching payMethods")
            const res = await fetchPaymentMethods(uid)
            return res ? res : []
        },
        staleTime,
        enabled: isEnabled
    })

    const getPaymentMethod = (booking: ResourceBookingSchema) => {
        let paymentMethodId = booking.payment_method
        return paymentMethods ? paymentMethods.find((obj: any) => obj['id'] === paymentMethodId) : {}

    }

    return { isLoadingPaymentMethods, paymentMethods, getPaymentMethod, reloadPaymentMethods}
}